import { matchPath } from "react-router-dom";

import type { TKey } from "@/lib/i18n";

/** The rail's items, by id — `AppShell`'s `activeId`. */
export type RailId =
  | "today"
  | "campaigns"
  | "reports"
  | "journal"
  | "health"
  | "offers"
  | "landers"
  | "partners"
  | "sources"
  | "domains"
  | "settings";

/** Each rail item's address and name. The library is five rows, not two tab strips (decision 03). */
export const RAIL_PATH: Record<RailId, string> = {
  today: "/",
  campaigns: "/campaigns",
  reports: "/stats",
  journal: "/journal/conversions",
  health: "/data-health",
  offers: "/library/offers",
  landers: "/library/landers",
  partners: "/library/networks",
  sources: "/library/sources",
  domains: "/domains",
  settings: "/settings",
};

export const RAIL_LABEL: Record<RailId, TKey> = {
  today: "nav.today",
  campaigns: "nav.campaigns",
  reports: "nav.reports",
  journal: "nav.journal",
  health: "nav.health",
  offers: "nav.offers",
  landers: "nav.landers",
  partners: "nav.partners",
  sources: "nav.sources",
  domains: "nav.domains",
  settings: "nav.settings",
};

/** What the second crumb is: the object the screen names (`useCrumbTail`), or a fixed word. */
export type CrumbTail = { kind: "object" } | { kind: "word"; key: TKey } | { kind: "objectThen"; key: TKey };

export interface Place {
  path: string;
  /** Which rail item is lit. Admin screens light none. */
  rail?: RailId;
  /** The first crumb. Linked back to the rail item whenever a second one follows. */
  crumb: TKey;
  tail?: CrumbTail;
  /** «Мои · Все» in the Top bar. */
  scope?: boolean;
  /** The period in the Top bar: every screen that shows numbers (decision 06). */
  period?: boolean;
}

const OBJECT: CrumbTail = { kind: "object" };
const word = (key: TKey): CrumbTail => ({ kind: "word", key });

/**
 * Where the Top bar is, and which of its controls the screen under it obeys —
 * drawn from the boards (the Top part's `crumb`, `scope` and `period` on each
 * of them). Order matters: the first match wins, so a detail path precedes its
 * list, and a fixed word («Изменить», «Новая») precedes the `:id` it shares a
 * shape with.
 */
export const PLACES: Place[] = [
  { path: "/", rail: "today", crumb: "nav.today", scope: true },

  { path: "/campaigns/new", rail: "campaigns", crumb: "nav.campaigns", tail: word("setup.crumbNew") },
  { path: "/campaigns/:id/edit", rail: "campaigns", crumb: "nav.campaigns", tail: { kind: "objectThen", key: "crumb.edit" } },
  { path: "/campaigns/:id/*", rail: "campaigns", crumb: "nav.campaigns", tail: OBJECT, scope: true, period: true },
  { path: "/campaigns", rail: "campaigns", crumb: "nav.campaigns", scope: true, period: true },

  { path: "/stats", rail: "reports", crumb: "nav.reports", scope: true, period: true },

  { path: "/journal/clicks", rail: "journal", crumb: "nav.journal", tail: word("journal.tab.clicks"), scope: true, period: true },
  { path: "/journal/conversions", rail: "journal", crumb: "nav.journal", tail: word("journal.tab.conversions"), scope: true, period: true },
  { path: "/journal/postbacks", rail: "journal", crumb: "nav.journal", tail: word("journal.tab.postbacks"), scope: true, period: true },
  { path: "/journal/bots", rail: "journal", crumb: "nav.journal", tail: word("journal.tab.bots"), scope: true, period: true },
  { path: "/journal/*", rail: "journal", crumb: "nav.journal", scope: true, period: true },

  { path: "/data-health", rail: "health", crumb: "nav.health", scope: true },

  { path: "/library/offers/:id/*", rail: "offers", crumb: "nav.offers", tail: OBJECT, scope: true, period: true },
  { path: "/library/offers", rail: "offers", crumb: "nav.offers", scope: true, period: true },
  // A lander's own numbers obey the period; its preview, versions and settings do not.
  { path: "/library/landers/:id/numbers", rail: "landers", crumb: "nav.landers", tail: OBJECT, scope: true, period: true },
  { path: "/library/landers/:id/usage", rail: "landers", crumb: "nav.landers", tail: OBJECT, scope: true, period: true },
  { path: "/library/landers/:id/*", rail: "landers", crumb: "nav.landers", tail: OBJECT, scope: true },
  { path: "/library/landers", rail: "landers", crumb: "nav.landers", scope: true, period: true },
  { path: "/library/networks/:id/*", rail: "partners", crumb: "nav.partners", tail: OBJECT, scope: true, period: true },
  { path: "/library/networks", rail: "partners", crumb: "nav.partners", scope: true, period: true },
  { path: "/library/sources/:id", rail: "sources", crumb: "nav.sources", tail: OBJECT, scope: true, period: true },
  { path: "/library/sources", rail: "sources", crumb: "nav.sources", scope: true, period: true },

  { path: "/domains", rail: "domains", crumb: "nav.domains" },

  { path: "/settings", rail: "settings", crumb: "nav.settings" },
  { path: "/settings/goals", rail: "settings", crumb: "nav.settings", tail: word("settings.tab.goals") },
  { path: "/settings/connections", rail: "settings", crumb: "nav.settings", tail: word("settings.tab.connections") },
  { path: "/settings/alerts", rail: "settings", crumb: "nav.settings", tail: word("settings.tab.alerts") },
  { path: "/settings/history", rail: "settings", crumb: "nav.settings", tail: word("settings.tab.history") },

  { path: "/admin/*", crumb: "nav.admin" },
];

/** The place an address is, or undefined. Pure. */
export function placeFor(pathname: string): Place | undefined {
  return PLACES.find((p) => matchPath({ path: p.path, end: true }, pathname));
}

export interface Crumb {
  label: string;
  href?: string;
}

/**
 * The Top bar's crumbs: the section, then what the screen is about. The first
 * links back to its rail item (period carried) only when something follows it.
 * Pure: `t` and the object's name come in.
 */
export function crumbsFor(
  place: Place | undefined,
  objectName: string,
  t: (key: TKey) => string,
  carried: string,
): Crumb[] {
  if (!place) return [];
  const head: Crumb = { label: t(place.crumb) };
  const tail = place.tail;
  const rest: Crumb[] = [];
  if (tail?.kind === "word") rest.push({ label: t(tail.key) });
  if (tail?.kind === "object" && objectName) rest.push({ label: objectName });
  if (tail?.kind === "objectThen") {
    if (objectName) rest.push({ label: objectName });
    rest.push({ label: t(tail.key) });
  }
  if (rest.length > 0 && place.rail) head.href = `${RAIL_PATH[place.rail]}${carried}`;
  return [head, ...rest];
}
